import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  createFreelancerApi,
  CreateFreelancerRequest,
  FreelancerPublic,
} from "../../../views/freelancer/freelancerApi";

import searchSvg from "../../../img/search.svg";

import { getStoresPaginatedApi, StorePublic } from "../../../views/store/storeApi";

type CreateFreelancerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (freelancer: FreelancerPublic) => void;
  token: string;
};

const STORES_LIMIT = 4;

const onlyDigits = (value: string) => value.replace(/\D/g, "");

const formatCpf = (value: string) => {
  const d = onlyDigits(value).slice(0, 11);
  const p1 = d.slice(0, 3);
  const p2 = d.slice(3, 6);
  const p3 = d.slice(6, 9);
  const p4 = d.slice(9, 11);

  let out = p1;
  if (p2) out += `.${p2}`;
  if (p3) out += `.${p3}`;
  if (p4) out += `-${p4}`;
  return out;
};

const formatPhoneBR = (value: string) => {
  const d = onlyDigits(value).slice(0, 11);
  const ddd = d.slice(0, 2);
  const p1 = d.slice(2, 7);
  const p2 = d.slice(7, 11);

  if (!ddd) return "";
  if (!p1) return `(${ddd}`;
  if (!p2) return `(${ddd}) ${p1}`;
  return `(${ddd}) ${p1}-${p2}`;
};

const normalizeUf = (uf?: string | null) => (uf ?? "").trim().toUpperCase();

const CreateFreelancerModal: React.FC<CreateFreelancerModalProps> = ({
  isOpen,
  onClose,
  onCreated,
  token,
}) => {
  const [nome, setNome] = useState("");
  const [telefone, setTelefone] = useState("");
  const [cpf, setCpf] = useState("");
  const [estado, setEstado] = useState<string>("");

  const [cidade, setCidade] = useState("");
  const [bairro, setBairro] = useState("");
  const [logradouro, setLogradouro] = useState("");
  const [numero, setNumero] = useState("");
  const [complemento, setComplemento] = useState("");

  const [chavePix, setChavePix] = useState("");
  const [banco, setBanco] = useState("");

  const [loading, setLoading] = useState(false);

  const [storesPage, setStoresPage] = useState(1);
  const [storesTotal, setStoresTotal] = useState(0);
  const [stores, setStores] = useState<StorePublic[]>([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [selectedStoreIds, setSelectedStoreIds] = useState<Set<string>>(new Set());
  const [storeSearchInput, setStoreSearchInput] = useState<string>("");
  const [storeSearch, setStoreSearch] = useState<string>("");

  const storesTotalPages = useMemo(
    () => Math.max(1, Math.ceil(storesTotal / STORES_LIMIT)),
    [storesTotal],
  );

  const canSubmit = useMemo(() => {
    const nOk = nome.trim().length > 0;
    const cOk = onlyDigits(cpf).length === 11;
    const eOk = estado.trim().length > 0;
    return nOk && cOk && eOk && !loading;
  }, [nome, cpf, estado, loading]);

  const fetchStores = useCallback(
    async (page: number) => {
      if (!token) return;
      try {
        setLoadingStores(true);
        const nome = storeSearch.trim();
        const data = await getStoresPaginatedApi(token, { page, limit: STORES_LIMIT, ...(nome ? { nome } : {}) });
        setStores(data.items ?? []);
        setStoresTotal(data.total ?? 0);
      } catch {
        toast.error("Erro ao buscar lojas.");
      } finally {
        setLoadingStores(false);
      }
    },
    [token, storeSearch],
  );

  const applyStoreSearch = () => {
    setStoresPage(1);
    setStoreSearch(storeSearchInput);
  };

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onClose();
    };

    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose, loading]);

  useEffect(() => {
    if (!isOpen) return;

    setNome("");
    setTelefone("");
    setCpf("");
    setEstado("");

    setCidade("");
    setBairro("");
    setLogradouro("");
    setNumero("");
    setComplemento("");

    setChavePix("");
    setBanco("");

    setLoading(false);

    setStoresPage(1);
    setStoresTotal(0);
    setStores([]);
    setSelectedStoreIds(new Set());
    setStoreSearchInput("");
    setStoreSearch("");

    if (!token) {
      toast.error("Sessão inválida. Faça login novamente.");
      onClose();
      return;
    }

    fetchStores(1);
  }, [isOpen, token, fetchStores, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    fetchStores(storesPage);
  }, [isOpen, storesPage, fetchStores]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedStoreIds(new Set());
  }, [estado, isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!loading) onClose();
  };

  const handleModalClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    e.stopPropagation();
  };

  const toggleStore = (store: StorePublic) => {
    const ufFreelancer = normalizeUf(estado);
    const ufStore = normalizeUf(store.estado);

    if (!ufFreelancer) {
      toast.error("Selecione o estado (UF) do freelancer antes.");
      return;
    }

    if (!ufStore) {
      toast.error("Esta loja não possui estado (UF) cadastrado.");
      return;
    }

    if (ufStore !== ufFreelancer) {
      toast.error(`Só é permitido selecionar lojas do estado ${ufFreelancer}.`);
      return;
    }

    setSelectedStoreIds((prev) => {
      const next = new Set(prev);
      if (next.has(store.id)) next.delete(store.id);
      else next.add(store.id);
      return next;
    });
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    if (!estado.trim()) {
      toast.error("Selecione o estado.");
      return;
    }
    if (!canSubmit) return;

    const trimmedNomeCompleto = nome.trim();
    const trimmedCpfDigits = onlyDigits(cpf);
    const trimmedContatoDigits = onlyDigits(telefone);

    const storeIds = Array.from(selectedStoreIds);

    const dto: CreateFreelancerRequest = {
      nomeCompleto: trimmedNomeCompleto,
      cpf: trimmedCpfDigits,
      contato: trimmedContatoDigits ? trimmedContatoDigits : undefined,
      pix: chavePix.trim() ? chavePix.trim() : undefined,
      banco: banco.trim() ? banco.trim() : undefined,
      rua: logradouro.trim() ? logradouro.trim() : undefined,
      numero: numero.trim() ? numero.trim() : undefined,
      complemento: complemento.trim() ? complemento.trim() : undefined,
      bairro: bairro.trim() ? bairro.trim() : undefined,
      cidade: cidade.trim() ? cidade.trim() : undefined,
      estado: estado.trim(),
      storeIds: storeIds.length ? storeIds : undefined,
    };

    try {
      setLoading(true);
      const freelancer = await createFreelancerApi(dto);
      toast.success("Freelancer cadastrado com sucesso!");
      onCreated?.(freelancer);
      onClose();
    } catch {
      toast.error("Erro ao cadastrar freelancer.");
    } finally {
      setLoading(false);
    }
  };

  const isLoadingStores = loadingStores;
  const ufFreelancer = normalizeUf(estado);

  return (
    <div className="ModalOverlay" onClick={handleOverlayClick} role="presentation">
      <div
        className="ModalShell"
        onClick={handleModalClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-freelancer-title"
      >
        <h2 className="ModalTitle" id="create-freelancer-title">
          Cadastrar Freelancer
        </h2>

        <form className="ModalBody" onSubmit={handleSubmit}>
          <div className="FreelancerPageSection">
            <h3 className="FreelancerPageSectionTitle">Dados pessoais</h3>

            <div
              className="FreelancerPageGrid"
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
            >
              <div className="ModalField">
                <label className="ModalLabel" htmlFor="freelancer-nome">
                  Nome Completo
                </label>
                <input
                  id="freelancer-nome"
                  className="ModalInput"
                  placeholder="Ex.: João da Silva"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  disabled={loading}
                  autoFocus
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="freelancer-telefone">
                  Telefone
                </label>
                <input
                  id="freelancer-telefone"
                  className="ModalInput"
                  placeholder="Ex.: (11) 91234-5678"
                  value={telefone}
                  onChange={(e) => setTelefone(formatPhoneBR(e.target.value))}
                  disabled={loading}
                  inputMode="tel"
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="freelancer-cpf">
                  CPF
                </label>
                <input
                  id="freelancer-cpf"
                  className="ModalInput"
                  placeholder="Ex.: 123.456.789-00"
                  value={cpf}
                  onChange={(e) => setCpf(formatCpf(e.target.value))}
                  disabled={loading}
                  inputMode="numeric"
                />
              </div>
            </div>
          </div>

          <div className="FreelancerPageSection">
            <h3 className="freelancer-section__title">Endereço</h3>

            <div
              className="FreelancerPageGrid"
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}
            >
              <div className="ModalField">
                <label className="ModalLabel" htmlFor="freelancer-logradouro">
                  Rua
                </label>
                <input
                  id="freelancer-logradouro"
                  className="ModalInput"
                  placeholder="Ex.: Rua Exemplo"
                  value={logradouro}
                  onChange={(e) => setLogradouro(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="freelancer-numero">
                  Número
                </label>
                <input
                  id="freelancer-numero"
                  className="ModalInput"
                  placeholder="Ex.: 123"
                  value={numero}
                  onChange={(e) => setNumero(e.target.value)}
                  disabled={loading}
                  inputMode="numeric"
                />
              </div>
            </div>

            <div
              className="FreelancerPageGrid"
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}
            >
              <div className="ModalField">
                <label className="ModalLabel" htmlFor="freelancer-complemento">
                  Complemento
                </label>
                <input
                  id="freelancer-complemento"
                  className="ModalInput"
                  placeholder="Ex.: Apto 12"
                  value={complemento}
                  onChange={(e) => setComplemento(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="freelancer-bairro">
                  Bairro
                </label>
                <input
                  id="freelancer-bairro"
                  className="ModalInput"
                  placeholder="Ex.: Centro"
                  value={bairro}
                  onChange={(e) => setBairro(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <div
              className="FreelancerPageGrid"
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}
            >
              <div className="ModalField">
                <label className="ModalLabel" htmlFor="freelancer-cidade">
                  Cidade
                </label>
                <input
                  id="freelancer-cidade"
                  className="ModalInput"
                  placeholder="Ex.: São Paulo"
                  value={cidade}
                  onChange={(e) => setCidade(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="freelancer-estado">
                  Estado
                </label>
                <select
                  id="freelancer-estado"
                  className="ModalInput"
                  value={estado}
                  onChange={(e) => setEstado(e.target.value)}
                  disabled={loading}
                  required
                >
                  <option value="" disabled>
                    Selecione...
                  </option>
                  <option value="RJ">RJ</option>
                  <option value="SP">SP</option>
                </select>
              </div>
            </div>
          </div>

          <div className="FreelancerPageSection">
            <h3 className="freelancer-section__title">Lojas</h3>

            <div className="Box" style={{ marginTop: 12 }}>
              <div className="BoxTitle">
                Selecione as lojas ({selectedStoreIds.size} selecionada
                {selectedStoreIds.size === 1 ? "" : "s"})
              </div>

              {!ufFreelancer ? (
                <div className="BoxEmpty">Selecione o estado (UF) do freelancer para habilitar a seleção.</div>
              ) : (
                <div className="FreelancerSearchRow">
                  <input
                    className="SlotInputSearch"
                    type="text"
                    value={storeSearchInput}
                    onChange={(e) => setStoreSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        applyStoreSearch();
                      }
                    }}
                    disabled={isLoadingStores || loading}
                  />
                  <button
                    type="button"
                    className="FreelancerSearchButton"
                    onClick={applyStoreSearch}
                    disabled={isLoadingStores || loading}
                  >
                    <img src={searchSvg} alt="Buscar" className="FreelancerSearchIcon" />
                  </button>
                </div>
              )}

              {!ufFreelancer ? null : isLoadingStores ? (
                <div className="BoxEmpty">Carregando lojas...</div>
              ) : stores.length === 0 ? (
                <div className="BoxEmpty">
                  {storeSearch.trim()
                    ? "Nenhuma loja encontrada para essa busca."
                    : "Nenhuma loja encontrada."}
                </div>
              ) : (
                <div className="List">
                  {stores.map((s) => {
                    const checked = selectedStoreIds.has(s.id);
                    const ufStore = normalizeUf(s.estado);
                    const disabledByUf = !ufStore || ufStore !== ufFreelancer;

                    return (
                      <button
                        key={s.id}
                        type="button"
                        className={`ListItem CheckItem ${checked ? "checked" : ""}`}
                        onClick={() => toggleStore(s)}
                        disabled={loading || disabledByUf}
                        title={
                          !ufStore
                            ? "Loja sem UF cadastrada"
                            : ufStore !== ufFreelancer
                              ? `Somente lojas do estado ${ufFreelancer}`
                              : undefined
                        }
                      >
                        <div className="CheckLeft">
                          <input type="checkbox" checked={checked} readOnly className="Checkbox" />
                        </div>

                        <div className="CheckRight">
                          <div className="ListItemTitle">
                            {s.nomeFantasia}
                            {ufStore ? ` • ${ufStore}` : " • UF não informada"}
                          </div>
                          <div className="ListItemSub">{s.endereco ? s.endereco : "Endereço não informado"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="Pager" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="PagerButton PagerButtonLight"
                disabled={isLoadingStores || storesPage <= 1 || loading || !ufFreelancer}
                onClick={() => setStoresPage((p) => Math.max(1, p - 1))}
              >
                Ant.
              </button>

              <div className="PagerInfo">
                {storesPage} de {storesTotalPages}
              </div>

              <button
                type="button"
                className="PagerButton"
                disabled={isLoadingStores || storesPage >= storesTotalPages || loading || !ufFreelancer}
                onClick={() => setStoresPage((p) => Math.min(storesTotalPages, p + 1))}
              >
                Próx.
              </button>
            </div>
          </div>

          <div className="FreelancerPageSection">
            <h3 className="freelancer-section__title">Pagamento</h3>

            <div
              className="FreelancerPageGrid"
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
            >
              <div className="ModalField">
                <label className="ModalLabel" htmlFor="freelancer-chavepix">
                  Chave Pix
                </label>
                <input
                  id="freelancer-chavepix"
                  className="ModalInput"
                  placeholder="Ex.: CPF, telefone ou chave aleatória"
                  value={chavePix}
                  onChange={(e) => setChavePix(e.target.value)}
                  disabled={loading}
                />
              </div>

              <div className="ModalField">
                <label className="ModalLabel" htmlFor="freelancer-banco">
                  Banco
                </label>
                <input
                  id="freelancer-banco"
                  className="ModalInput"
                  placeholder="Ex.: Itaú"
                  value={banco}
                  onChange={(e) => setBanco(e.target.value)}
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className="ModalActions">
            <button
              type="button"
              className="ModalButton ModalButtonCancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>

            <button type="submit" className="ModalButton ModalButtonConfirm" disabled={!canSubmit}>
              {loading ? "Salvando..." : "Confirmar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFreelancerModal;
